import { useState, useEffect, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { useAuth } from '@/hooks/useAuth';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { computeDiscountedPrice } from '@/utils/pricing';
import { Check, CreditCard, ExternalLink } from 'lucide-react';

// Generate idempotency key for payment session
const generateIdempotencyKey = (courseId: string, userId: string, type: 'stripe' | 'cart') => {
  return `${type}_${courseId}_${userId}_${Date.now()}`;
};

interface PaymentOptionsProps {
  courseId: string;
  formationTitle: string;
  price: number;
  currency?: string;
  isCart?: boolean;
  cartItems?: Array<{id: string; title: string; price: number; currency: string}>;
}

export function PaymentOptions({ 
  courseId, 
  formationTitle, 
  price, 
  currency = "eur", 
  isCart = false, 
  cartItems = [] 
}: PaymentOptionsProps) {
  const [loading, setLoading] = useState(false);
  const [actualPrice, setActualPrice] = useState(price);
  const [fallbackUrl, setFallbackUrl] = useState<string | null>(null);
  const { user } = useAuth();
  const { toast } = useToast();
  const idempotencyKeyRef = useRef<string | null>(null);

  // Recalculate price from database to ensure promotions are applied
  useEffect(() => {
    const recalculatePrice = async () => {
      if (isCart || !courseId || courseId === 'cart') {
        setActualPrice(price);
        return;
      }

      try {
        const { data: courseData, error } = await supabase
          .rpc('get_public_course_info')
          .eq('id', courseId)
          .maybeSingle();

        if (error || !courseData) {
          console.error('Error fetching course data for pricing:', error);
          setActualPrice(price);
          return;
        }

        const originalPrice = Number(courseData.price_ttc ?? 0);
        const promotions = Array.isArray(courseData.promotions) ? courseData.promotions : 
                          (typeof courseData.promotions === 'string' ? JSON.parse(courseData.promotions) : []);
        const pricingInfo = computeDiscountedPrice(originalPrice, promotions);
        setActualPrice(pricingInfo.discountedPrice);
        
        console.log('💰 Price recalculated:', {
          originalPrice,
          discountedPrice: pricingInfo.discountedPrice,
          hasDiscount: pricingInfo.hasDiscount
        });
      } catch (error) {
        console.error('Error recalculating price:', error);
        setActualPrice(price);
      }
    };

    recalculatePrice();
  }, [courseId, price, isCart]);

  // Robust Stripe redirection pattern
  const handleStripeRedirection = (checkoutUrl: string) => {
    const isInIframe = window.self !== window.top;
    
    if (isInIframe) {
      const newWindow = window.open(checkoutUrl, '_blank');
      if (!newWindow || newWindow.closed) {
        setFallbackUrl(checkoutUrl);
        setLoading(false);
        toast({
          title: 'Popup bloquée',
          description: 'Cliquez sur le lien ci-dessous pour accéder au paiement.',
        });
        return;
      }
    } else {
      try {
        window.location.replace(checkoutUrl);
      } catch {
        window.location.href = checkoutUrl;
      }
    }
    
    setTimeout(() => {
      if (!fallbackUrl) {
        setFallbackUrl(checkoutUrl);
        setLoading(false);
      }
    }, 1500);
  };

  const handleStripePayment = async () => {
    if (!user) {
      toast({
        title: "Connexion requise",
        description: "Vous devez être connecté pour effectuer un paiement",
        variant: "destructive",
      });
      return;
    }

    // Generate idempotency key if not already set
    if (!idempotencyKeyRef.current) {
      idempotencyKeyRef.current = generateIdempotencyKey(courseId, user.id, isCart ? 'cart' : 'stripe');
    }

    setLoading(true);
    setFallbackUrl(null);
    
    try {
      console.log('🔄 Initiating payment for:', { courseId, formationTitle, price, isCart });
      
      const functionName = isCart ? 'create-cart-checkout' : 'create-stripe-checkout';
      const body = isCart ? {
        items: cartItems.map(item => ({
          courseId: item.id,
          formationTitle: item.title,
          amount: Math.round(item.price),
          currency: item.currency
        })),
        totalAmount: Math.round(price),
        currency: currency.toLowerCase(),
        idempotencyKey: idempotencyKeyRef.current,
      } : {
        courseId,
        formationTitle,
        amount: Math.round(actualPrice),
        currency: currency.toLowerCase(),
        idempotencyKey: idempotencyKeyRef.current,
      };
      
      const { data, error } = await supabase.functions.invoke(functionName, { body });

      if (error) {
        console.error('❌ Stripe function error:', error);
        throw new Error(error.message || 'Erreur lors de la création de la session de paiement');
      }

      if (!data?.url) {
        throw new Error('URL de paiement non reçue');
      }

      console.log('✅ Stripe session created, redirecting to:', data.url);
      handleStripeRedirection(data.url);
      
    } catch (error: any) {
      console.error('❌ Stripe payment error:', error);
      toast({
        title: "Erreur de paiement",
        description: error.message || "Erreur lors de la création de la session de paiement",
        variant: "destructive",
      });
      setLoading(false);
    }
  };

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: currency.toUpperCase()
    }).format(price);
  };

  return (
    <div className="space-y-6 max-w-lg mx-auto">
      {/* Prix et informations de base */}
      <div className="text-center space-y-3">
        <h2 className="text-2xl font-bold text-gray-900">{formationTitle}</h2>
        <div className="text-5xl font-bold text-primary">
          {formatPrice(actualPrice)}
        </div>
        <p className="text-gray-600">Formation complète • Accès immédiat • Certificat inclus</p>
      </div>

      {/* Paiement principal */}
      <div className="space-y-4">
        <Card className="border-2 border-primary">
          <CardContent className="pt-6">
            <div className="text-center space-y-4">
              <div className="flex items-center justify-center gap-3 mb-4">
                <CreditCard className="h-6 w-6 text-primary" />
                <span className="font-semibold text-lg">Paiement sécurisé</span>
              </div>
              
              <Button
                onClick={handleStripePayment}
                disabled={loading}
                size="lg"
                className="w-full text-lg font-semibold h-14"
              >
                {loading ? "Préparation..." : `Payer maintenant - ${formatPrice(actualPrice)}`}
              </Button>
              
              <div className="text-sm text-gray-500 space-y-1">
                <p>• Paiement sécurisé par Stripe</p>
                <p>• Paiement fractionné disponible avec Klarna</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Lien pour autres modes de paiement */}
      <div className="text-center">
        <Button
          variant="outline"
          onClick={() => window.open('/financement-opco', '_blank')}
          className="w-full"
        >
          Autres modes de financement (OPCO, entreprise...)
        </Button>
      </div>

      {/* Fallback link */}
      {fallbackUrl && (
        <Card className="p-4 border-orange-200 bg-orange-50">
          <p className="text-sm mb-2">La redirection automatique n'a pas fonctionné.</p>
          <a 
            href={fallbackUrl} 
            target="_blank" 
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 text-primary font-medium hover:underline"
          >
            Cliquez ici pour accéder au paiement
            <ExternalLink className="h-4 w-4" />
          </a>
        </Card>
      )}

      {/* Garanties */}
      <div className="bg-gray-50 rounded-lg p-4 text-center">
        <div className="flex items-center justify-center gap-2 mb-2">
          <Check className="h-4 w-4 text-green-600" />
          <span className="font-medium text-sm">Garanties</span>
        </div>
        <div className="text-xs text-gray-600 space-y-1">
          <p><Check className="h-3 w-3 inline mr-1" /> Accès immédiat après paiement</p>
          <p><Check className="h-3 w-3 inline mr-1" /> Certificat de formation inclus</p>
        </div>
      </div>
    </div>
  );
}